import { useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { usePredictionMarketEventsFindLobby } from '@/lib/endpoints';
import { Skeleton } from '@/components/ui';
import { cn } from '@/lib/utils';
import type { OrderSide } from '@/analytics';
import type { EventCardDto, MarketOptionDto } from '@/lib/models';
import { BinaryQuestionCard } from '../BinaryMarkets/BinaryQuestionCard';
import { BinaryQuestionCardSkeleton } from '../BinaryMarkets/BinaryQuestionCardSkeleton';
import { BuyInPopup, type BuyMarketContext } from '../BinaryMarkets/BuyInPopup';
import { SportsMoneylineCard } from '../SportsMarkets/SportsMoneylineCard';

/** Popup target carrying the resolved option context + opening side. */
interface BuyTarget {
  context: BuyMarketContext;
  side: OrderSide;
}

/** How many cards the recommended rail shows (design shows a 5-dot rail). */
const CAROUSEL_SIZE = 5;
/**
 * Slide sizing: mobile shows one card with the next peeking (per Figma
 * 1557-50407); md+ shows two per view like the original hero carousel.
 * The `pl-3` on each slide + `-ml-3` on the container is Embla's
 * margin-based 12px gap pattern.
 */
const SLIDE_CLASS = 'min-w-0 shrink-0 basis-[88%] md:basis-1/2 pl-3';

/**
 * "Just for you" recommended carousel at the top of the trending lobby
 * (Figma 1557-50407 "Banner Lobby"): an Embla slide rail of question cards,
 * each wearing the purple corner ribbon, with a pagination-dot rail
 * underneath. Keeps the original hero-carousel behavior — auto-advances
 * every 5s (looping, paused while hovered), swipeable, dots jump to a slide.
 *
 * There is no recommendation engine yet — the rail previews the first
 * {@link CAROUSEL_SIZE} events of the trending lobby feed (same
 * `usePredictionMarketEventsFindLobby({limit: 50})` fetch as
 * `CategorySections`, so React Query serves both from one request).
 */
export function RecommendedCarousel() {
  const { data, isLoading } = usePredictionMarketEventsFindLobby({ limit: 50 });
  const [buyTarget, setBuyTarget] = useState<BuyTarget | null>(null);
  const [activeDot, setActiveDot] = useState(0);

  const [emblaRef, emblaApi] = useEmblaCarousel({ align: 'start', loop: true, duration: 15 }, [
    Autoplay({ delay: 5000, stopOnInteraction: false, stopOnMouseEnter: true }),
  ]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setActiveDot(emblaApi.selectedScrollSnap());
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi]);

  const events = (data?.events ?? []).slice(0, CAROUSEL_SIZE);

  function handleOpenBuy(event: EventCardDto, option: MarketOptionDto, side: OrderSide) {
    setBuyTarget({ context: { ...option, eventName: event.name }, side });
  }

  if (isLoading) {
    return (
      <div className="flex w-full flex-col gap-2">
        <div className="flex w-full gap-3 overflow-hidden">
          {[1, 2].map((i) => (
            <div key={i} className="min-w-0 shrink-0 basis-[88%] md:basis-1/2">
              <BinaryQuestionCardSkeleton />
            </div>
          ))}
        </div>
        {/* Reserve the pagination-dot rail so the feed doesn't shift on load. */}
        <div className="flex items-center justify-center gap-1">
          {Array.from({ length: CAROUSEL_SIZE }).map((_, i) => (
            <Skeleton key={i} className={cn('h-[5px] rounded-pill', i === 0 ? 'w-[21px]' : 'w-[7px]')} />
          ))}
        </div>
      </div>
    );
  }

  if (events.length === 0) return null;

  return (
    <div className="flex w-full flex-col gap-2">
      {/* Embla viewport — clips at the feed column edge, like the original hero. */}
      <div ref={emblaRef} className="w-full overflow-hidden">
        <div className="-ml-3 flex items-stretch">
          {events.map((event) => (
            <div key={event.id} className={SLIDE_CLASS}>
              {/* The corner ribbon owns the top-right 24px — shift the countdown notch below it. */}
              <div className="relative h-full [&_.countdown-badge]:top-[30px]">
                {event.sportsMoneyline ? (
                  <SportsMoneylineCard
                    event={event}
                    moneyline={event.sportsMoneyline}
                    onOpenBuy={(option, side) => handleOpenBuy(event, option, side)}
                  />
                ) : (
                  <BinaryQuestionCard event={event} onOpenBuy={(option, side) => handleOpenBuy(event, option, side)} />
                )}
                {/* "Just for you" corner ribbon (Figma 6713:191914). */}
                <div
                  aria-hidden
                  className="pointer-events-none absolute right-0 top-0 flex h-6 w-[91px] items-center justify-center rounded-bl-[12px] rounded-tr-[12px] bg-purple"
                >
                  <span className="text-[12px] font-medium tracking-[-0.24px] text-white">Just for you</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Pagination dots — active dot stretches, inactive sit at 50%. */}
      <div className="flex items-center justify-center gap-1">
        {events.map((event, i) => (
          <button
            key={event.id}
            type="button"
            aria-label={`Go to card ${i + 1}`}
            aria-current={i === activeDot}
            onClick={() => emblaApi?.scrollTo(i)}
            className={cn(
              'h-[5px] rounded-pill bg-purple border-none p-0 transition-all duration-200',
              i === activeDot ? 'w-[21px]' : 'w-[7px] opacity-50',
            )}
          />
        ))}
      </div>

      <BuyInPopup
        market={buyTarget?.context ?? null}
        initialSide={buyTarget?.side ?? 'yes'}
        surface="home.card"
        onClose={() => setBuyTarget(null)}
      />
    </div>
  );
}
